import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search, TrendingUp, TrendingDown, Minus, Filter, Download,
  Globe, Loader2, Plus, ArrowRight, BarChart3, Target,
  CheckCircle, XCircle
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Badge from '../../components/ui/Badge';
import { keywordService } from '../../services/keywordService';
import toast from 'react-hot-toast';

interface KeywordResult {
  keyword: string;
  searchVolume: number;
  difficulty: number;
  cpc: number;
  competition: 'low' | 'medium' | 'high';
  trend: 'up' | 'down' | 'stable';
  intent: 'informational' | 'transactional' | 'navigational' | 'commercial';
  serpFeatures: string[];
  topRankingUrls?: { url: string; position: number; domain: string }[];
}

interface DomainKeywords {
  domain: string;
  totalKeywords: number;
  organicTraffic: number;
  keywords: {
    keyword: string;
    position: number;
    searchVolume: number;
    traffic: number;
    url: string;
    change: number;
  }[];
}

type SearchType = 'keyword' | 'domain';

const KeywordsExplorerPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchType, setSearchType] = useState<SearchType>('keyword');
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Keyword search results
  const [keywordResult, setKeywordResult] = useState<KeywordResult | null>(null);

  // Domain search results
  const [domainKeywords, setDomainKeywords] = useState<DomainKeywords | null>(null);

  // Filters for domain keywords
  const [positionFilter, setPositionFilter] = useState('all');
  const [minVolume, setMinVolume] = useState('');

  // Selected keywords for import
  const [selectedKeywords, setSelectedKeywords] = useState<Set<string>>(new Set());

  const INTENT_COLORS: Record<string, string> = {
    informational: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    transactional: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    navigational: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    commercial: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
  };

  const SERP_FEATURES = ['Featured Snippet', 'People Also Ask', 'Local Pack', 'Images', 'Videos', 'Shopping', 'Knowledge Panel', 'Reviews'];

  const handleSearch = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!query.trim()) return;

    setIsLoading(true);
    setSelectedKeywords(new Set());

    try {
      if (searchType === 'keyword') {
        const response = await keywordService.exploreKeyword(query.trim());
        if (response.success && response.data) {
          setKeywordResult(response.data);
        } else {
          toast.error(t('keywords.noDataFound', 'No data found for this keyword.'));
          setKeywordResult(null);
        }
        setDomainKeywords(null);
      } else {
        const response = await keywordService.exploreDomainKeywords(query.trim());
        if (response.success && response.data) {
          setDomainKeywords(response.data);
        } else {
          toast.error(t('competitors.noDataFound', 'No data found for this domain.'));
          setDomainKeywords(null);
        }
        setKeywordResult(null);
      }
    } catch (error) {
      console.error('Error searching:', error);
      toast.error(t('errors.generic'));
      setKeywordResult(null);
      setDomainKeywords(null);
    } finally {
      setIsLoading(false);
    }
  }, [query, searchType, t]);

  const handleToggleKeyword = (keyword: string) => {
    const newSelected = new Set(selectedKeywords);
    if (newSelected.has(keyword)) {
      newSelected.delete(keyword);
    } else {
      newSelected.add(keyword);
    }
    setSelectedKeywords(newSelected);
  };

  const handleImportSelected = async () => {
    if (selectedKeywords.size === 0) return;
    try {
      await keywordService.createBulkKeywords(Array.from(selectedKeywords));
      toast.success(t('keywords.keywordsImported', `${selectedKeywords.size} keywords imported`));
      setSelectedKeywords(new Set());
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const getTrendIcon = (trend: string) => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (trend === 'down') return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const getDifficultyColor = (difficulty: number) => {
    if (difficulty < 30) return 'text-green-500';
    if (difficulty < 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getFilteredKeywords = () => {
    if (!domainKeywords) return [];
    let filtered = domainKeywords.keywords;

    if (positionFilter !== 'all') {
      const [min, max] = positionFilter.split('-').map(Number);
      filtered = filtered.filter(k => k.position >= min && k.position <= max);
    }

    if (minVolume) {
      filtered = filtered.filter(k => k.searchVolume >= parseInt(minVolume));
    }

    return filtered;
  };

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
          {t('nav.sections.keywords', 'Palavras-chave')}
        </h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
          {t('competitors.keywordsExplorerDescription', 'Search keywords or explore all keywords from a domain')}
        </p>
      </div>

      {/* Search */}
      <Card>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <div className="grid grid-cols-2 sm:flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setSearchType('keyword')}
                className={`px-3 sm:px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  searchType === 'keyword'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-300'
                }`}
              >
                <Search className="w-4 h-4 inline mr-2" />
                {t('competitors.byKeyword', 'By Keyword')}
              </button>
              <button
                onClick={() => setSearchType('domain')}
                className={`px-3 sm:px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  searchType === 'domain'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-300'
                }`}
              >
                <Globe className="w-4 h-4 inline mr-2" />
                {t('competitors.byDomain', 'By Domain')}
              </button>
            </div>

            <form onSubmit={handleSearch} className="flex-1 flex gap-2 sm:gap-4">
              <div className="flex-1 min-w-0">
                <Input
                  placeholder={
                    searchType === 'keyword'
                      ? t('competitors.enterKeyword', 'Enter a keyword')
                      : t('competitors.enterDomain', 'Enter a domain')
                  }
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  leftIcon={searchType === 'keyword' ? <Search className="w-5 h-5" /> : <Globe className="w-5 h-5" />}
                />
              </div>
              <Button type="submit" disabled={isLoading || !query.trim()} className="whitespace-nowrap flex-shrink-0">
                {isLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                {t('common.search', 'Search')}
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary-500 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              {t('common.searching', 'Searching...')}
            </p>
          </div>
        </div>
      )}

      {/* Keyword Result */}
      {keywordResult && !isLoading && (
        <div className="space-y-3 sm:space-y-6">
          {/* Keyword Overview */}
          <Card>
            <CardContent className="sm:pt-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4 sm:mb-6">
                <div className="min-w-0">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2 break-words">
                    {keywordResult.keyword}
                  </h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs px-2 py-1 rounded-full ${INTENT_COLORS[keywordResult.intent]}`}>
                      {t(`keywords.intent.${keywordResult.intent}`, keywordResult.intent)}
                    </span>
                    {getTrendIcon(keywordResult.trend)}
                    <Badge variant={keywordResult.competition === 'low' ? 'success' : keywordResult.competition === 'medium' ? 'warning' : 'danger'}>
                      {t(`keywords.competitionLevel.${keywordResult.competition}`)}
                    </Badge>
                  </div>
                </div>
                <Button onClick={() => {
                  keywordService.createKeyword({ keyword: keywordResult.keyword });
                  toast.success(t('keywords.keywordAdded', 'Keyword added'));
                }} className="h-10 whitespace-nowrap self-start flex-shrink-0">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('keywords.addKeyword', 'Add Keyword')}
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 sm:p-4 min-w-0">
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1 truncate">
                    {t('keywords.searchVolume')}
                  </p>
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                    {formatNumber(keywordResult.searchVolume)}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 sm:p-4 min-w-0">
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1 truncate">
                    {t('keywords.difficulty')}
                  </p>
                  <p className={`text-lg sm:text-2xl font-bold ${getDifficultyColor(keywordResult.difficulty)}`}>
                    {keywordResult.difficulty}/100
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 sm:p-4 min-w-0">
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1 truncate">
                    {t('keywords.cpc')}
                  </p>
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                    R$ {keywordResult.cpc.toFixed(2)}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 sm:p-4 min-w-0">
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1 truncate">
                    SERP Features
                  </p>
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                    {keywordResult.serpFeatures.length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* SERP Features */}
          {keywordResult.serpFeatures.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{t('keywords.serpFeatures.title', 'O que aparece no Google')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {keywordResult.serpFeatures.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-lg"
                    >
                      <CheckCircle className="w-4 h-4" />
                      {feature}
                    </div>
                  ))}
                  {SERP_FEATURES.filter(f => !keywordResult.serpFeatures.includes(f)).slice(0, 3).map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-lg"
                    >
                      <XCircle className="w-4 h-4" />
                      {feature}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Top Ranking URLs */}
          <Card>
            <CardHeader>
              <CardTitle>{t('keywords.topRankingUrls', 'Top 10 Ranking URLs')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {keywordResult.topRankingUrls?.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`w-8 h-8 flex-shrink-0 flex items-center justify-center font-bold rounded ${
                        item.position <= 3 ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                        item.position <= 10 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                        'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                      }`}>
                        {item.position}
                      </span>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
                          {item.domain}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[150px] sm:max-w-[300px]">
                          {item.url}
                        </p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setQuery(item.domain)} aria-label={t('common.analyze', 'Analyze')} className="flex-shrink-0 whitespace-nowrap">
                      <span className="hidden sm:inline">{t('common.analyze', 'Analyze')}</span>
                      <ArrowRight className="w-4 h-4 sm:ml-1" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Domain Keywords Result */}
      {domainKeywords && !isLoading && (
        <div className="space-y-3 sm:space-y-6">
          {/* Domain Overview */}
          <Card>
            <CardContent className="sm:pt-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4 sm:mb-6">
                <div className="min-w-0">
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-1 break-all">
                    {domainKeywords.domain}
                  </h2>
                  <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
                    {formatNumber(domainKeywords.totalKeywords)} {t('keywords.organicKeywords', 'organic keywords')} | {formatNumber(domainKeywords.organicTraffic)} {t('keywords.estimatedTraffic', 'estimated traffic')}
                  </p>
                </div>
                {selectedKeywords.size > 0 && (
                  <Button onClick={handleImportSelected} className="whitespace-nowrap">
                    <Download className="w-4 h-4 mr-2" />
                    {t('keywords.importSelected', 'Import')} ({selectedKeywords.size})
                  </Button>
                )}
              </div>

              {/* Filters */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 p-3 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {t('common.filters', 'Filters')}:
                  </span>
                </div>
                <Select
                  value={positionFilter}
                  onChange={(e) => setPositionFilter(e.target.value)}
                  options={[
                    { value: 'all', label: t('keywords.allPositions', 'All positions') },
                    { value: '1-3', label: 'Top 3' },
                    { value: '1-10', label: 'Top 10' },
                    { value: '11-20', label: '11-20' },
                    { value: '21-50', label: '21-50' },
                    { value: '51-100', label: '51-100' }
                  ]}
                />
                <Input
                  placeholder={t('keywords.minVolume', 'Min volume')}
                  value={minVolume}
                  onChange={(e) => setMinVolume(e.target.value)}
                  type="number"
                  className="w-32"
                />
              </div>
            </CardContent>
          </Card>

          {/* Keywords Table */}
          <Card padding={false}>
            <div className="overflow-x-auto scrollbar-hide">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="w-10 px-2 sm:px-4 py-2 sm:py-3">
                      <input
                        type="checkbox"
                        className="rounded"
                        checked={selectedKeywords.size === getFilteredKeywords().length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedKeywords(new Set(getFilteredKeywords().map(k => k.keyword)));
                          } else {
                            setSelectedKeywords(new Set());
                          }
                        }}
                      />
                    </th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      {t('keywords.keyword')}
                    </th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      {t('keywords.position')}
                    </th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      {t('keywords.searchVolume')}
                    </th>
                    <th className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      {t('keywords.traffic', 'Traffic')}
                    </th>
                    <th className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      URL
                    </th>
                    <th className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      {t('keywords.change', 'Change')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {getFilteredKeywords().map((keyword, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-2 sm:px-4 py-2 sm:py-3">
                        <input
                          type="checkbox"
                          className="rounded"
                          checked={selectedKeywords.has(keyword.keyword)}
                          onChange={() => handleToggleKeyword(keyword.keyword)}
                        />
                      </td>
                      <td className="px-2 sm:px-4 py-2 sm:py-3 font-medium text-gray-900 dark:text-white break-words">
                        {keyword.keyword}
                      </td>
                      <td className="px-2 sm:px-4 py-2 sm:py-3">
                        <Badge
                          variant={keyword.position <= 3 ? 'success' : keyword.position <= 10 ? 'warning' : 'gray'}
                        >
                          #{keyword.position}
                        </Badge>
                      </td>
                      <td className="px-2 sm:px-4 py-2 sm:py-3 text-gray-600 dark:text-gray-400">
                        {formatNumber(keyword.searchVolume)}
                      </td>
                      <td className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 text-gray-600 dark:text-gray-400">
                        {formatNumber(keyword.traffic)}
                      </td>
                      <td className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 text-gray-600 dark:text-gray-400 text-sm truncate max-w-[150px]">
                        {keyword.url}
                      </td>
                      <td className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3">
                        <span className={`flex items-center gap-1 ${
                          keyword.change > 0 ? 'text-green-500' :
                          keyword.change < 0 ? 'text-red-500' : 'text-gray-400'
                        }`}>
                          {keyword.change > 0 ? <TrendingUp className="w-4 h-4" /> :
                           keyword.change < 0 ? <TrendingDown className="w-4 h-4" /> :
                           <Minus className="w-4 h-4" />}
                          {keyword.change !== 0 && Math.abs(keyword.change)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* Empty State */}
      {!keywordResult && !domainKeywords && !isLoading && (
        <Card>
          <CardContent className="text-center py-16">
            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {t('keywords.exploreKeywords', 'Explore Keywords')}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              {t('keywords.explorerEmptyState', 'Search a keyword to see volume, difficulty and ranking URLs, or enter a domain to see all its organic keywords.')}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default KeywordsExplorerPage;
